import { axios, instanceBo } from "../utils/axios.create";
import {
	BeneficiaryItem,
	BeneficiaryItemSearched,
	BeneficiaryPayload,
	BeneficiaryResponse,
	BeneficiaryVisitData,
	Program,
	Provider,
	ProviderSite,
	IWeekStatus,
	ConsentFormData,
	Consent,
	ConsentPayload,
	ConsentUrlResponse,
	BeneficiaryEnrollmentPeriodsResponse,
	BeneficiaryIncentiveHistoryResponse,
	ParamsDeleteCard,
	ICardDelete,
	CardData,
	CardPayload,
	CardPinPayload,
} from "../domain/beneficiary.interface";
import dayjs from "dayjs";
import { TreeItem } from "../domain/common.interface";

const findAll = async ({
	providerSite,
	programId,
	providerId,
	userId,
	status,
	search,
	pageNumber,
	pageSize,
}: {
	providerSite: string[];
	programId: string;
	providerId: string;
	userId: string;
	status: string[];
	search: string;
	pageNumber: number;
	pageSize: number;
}): Promise<{
	data: BeneficiaryResponse[];
	total: number;
	totalCard: number;
	totalActive: number;
	totalDisenroll: number;
	totalCompleted: number;
	totalPaused: number;
}> => {
	programId = programId === "all" ? "" : programId;
	providerId = providerId === "all" ? "" : providerId;

	return axios
		.get(
			`/beneficiaries/information?providerSite=${providerSite}&programId=${programId}&providerId=${providerId}&status=${status.join(",")}&search=${search}&pageNumber=${pageNumber}&pageSize=${pageSize}&userId=${userId}`,
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

/** Get beneficiary list from backend 2 with program+OU combination filtering */
const findAllV2 = async ({
	providerSite,
	programId,
	providerId,
	userId,
	status,
	search,
	pageNumber,
	pageSize,
}: {
	providerSite: string[];
	programId: string;
	providerId: string;
	userId: string;
	status: string[];
	search: string;
	pageNumber: number;
	pageSize: number;
}): Promise<{
	data: BeneficiaryResponse[];
	total: number;
	totalCard: number;
	totalActive: number;
	totalDisenroll: number;
	totalCompleted: number;
	totalPaused: number;
}> => {
	const params = new URLSearchParams();

	if (userId) params.append("userId", userId);
	if (programId && programId !== "all") params.append("programIds", programId);
	if (providerSite.length > 0)
		params.append("orgUnitIds", providerSite.join(","));
	if (providerId && providerId !== "all")
		params.append("assignedUserId", providerId);
	if (status.length > 0) params.append("status", status.join(","));
	if (search) params.append("search", search);
	params.append("pageNumber", String(pageNumber));
	params.append("pageSize", String(pageSize));

	return instanceBo
		.get(`beneficiaries/list?${params.toString()}`)
		.then((res) => {
			const body = res.data.body;
			return {
				data: body.data,
				total: body.total,
				totalCard: body.totalCard,
				totalActive: body.totalActive,
				totalCompleted: body.totalCompleted,
				totalDisenroll: body.totalDisenroll,
				totalPaused: body.totalPaused || 0,
			};
		})
		.catch((err) => Promise.reject(err?.response?.data || err));
};

const findById = async (
	beneficiaryId: string,
	enrollmentId?: string,
): Promise<BeneficiaryResponse> => {
	let enrollmentIdParam = "";

	if (enrollmentId && enrollmentId.trim() !== "") {
		enrollmentIdParam = `?enrollmentId=${enrollmentId}`;
	}

	return axios
		.get(
			`/beneficiaries/beneficiary/information/${beneficiaryId}${enrollmentIdParam}`,
		)
		.then((res) => res.data.body[0])
		.catch((err) => Promise.reject(err.response.data));
};

/**
 * Get beneficiary info from backend 2 with cups/analytes data populated
 */
const findByIdV2 = async (
	beneficiaryId: string,
	enrollmentId?: string,
): Promise<BeneficiaryResponse> => {
	let enrollmentIdParam = "";

	if (enrollmentId && enrollmentId.trim() !== "") {
		enrollmentIdParam = `?enrollmentId=${enrollmentId}`;
	}

	return instanceBo
		.get(`beneficiaries/${beneficiaryId}${enrollmentIdParam}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const create = async (
	data: BeneficiaryPayload,
): Promise<{ beneficiaryId: string; enrollmentId: string }> => {
	return axios
		.post("/beneficiaries", data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const update = async (
	data: Partial<BeneficiaryPayload>,
	beneficiaryId: string,
	enrollmentId: string,
): Promise<BeneficiaryItem> => {
	return axios
		.put(`/beneficiaries/${beneficiaryId}/${enrollmentId}`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const patchBeneficiary = async (
	beneficiaryId: string,
	data: Partial<Pick<BeneficiaryPayload, "userId" | "phone" | "email">>,
): Promise<BeneficiaryItem> => {
	return axios
		.patch(`/beneficiaries/patch/${beneficiaryId}`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const updateReconfirm = async (
	userId: string,
	beneficiaryId: string,
	enrollmentId: string,
): Promise<BeneficiaryItem> => {
	return axios
		.put(
			`/beneficiaries/reconfirmeligibility/${beneficiaryId}/${enrollmentId}`,
			{
				userId,
			},
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const getConsentForms = async (
	beneficiaryId: string,
): Promise<ConsentFormData[]> => {
	return axios
		.get(`/beneficiaries/${beneficiaryId}/consents`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const patchConsentForm = async (
	data: ConsentPayload,
	beneficiaryId: string,
	enrollmentId: string,
): Promise<Consent> => {
	return axios
		.patch(
			`/beneficiaries/${beneficiaryId}/enrollments/${enrollmentId}/consent`,
			data,
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const getConsentFormUrl = async (
	s3Url: string,
): Promise<ConsentUrlResponse> => {
	const urlParams = new URLSearchParams();
	urlParams.append("s3Url", s3Url);
	return axios
		.get(`/consent-file?${urlParams.toString()}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const findPrograms = async (programIds: string[]): Promise<Program[]> => {
	return axios
		.get(`/beneficiaries/programs?userProgramIds=${programIds.join(",")}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

/** Get programs by IDs from backend 2 */
const findProgramsV2 = async (programIds: string[]): Promise<Program[]> => {
	return instanceBo
		.get(`beneficiaries/programs?ids=${programIds.join(",")}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err?.response?.data || err));
};

const findProviderSites = async (
	ouIds: string[],
): Promise<
	{
		key: string;
		id: string;
		value: string;
		name: string;
		children: TreeItem[];
		groupLabelIds?: string[];
	}[]
> => {
	if (ouIds.length === 0) {
		return [];
	}

	// Use comma-separated values instead of ids[]=value format
	// API Gateway doesn't properly handle duplicate parameter names
	const idsParam = ouIds.join(",");

	return instanceBo
		.get(`/organizations/providers-site?ids=${idsParam}`)
		.then((res) => {
			const providers = res.data.body;

			return providers.map((providerSite: ProviderSite) => {
				return {
					key: providerSite._id,
					id: providerSite._id,
					value: providerSite.name,
					name: providerSite.name,
					groupLabelIds: providerSite.groupLabelIds || [],
				};
			});
		})
		.catch((err) => Promise.reject(err.response.data));
};

const findProviders = async (
	programId: string[],
	orgUnitId: string[],
	includeAll: boolean,
): Promise<{ key: string; value: string; roles: string[] }[]> => {
	return axios
		.get(
			`/beneficiaries/providers?orgUnitIds=${orgUnitId.join(",")}&programIds=${programId.join(",")}`,
		)
		.then((res) => {
			const providers = res.data.body as Provider[];
			const optionAll = includeAll
				? [{ key: "all", value: "All", roles: [] }]
				: [];

			return [
				...optionAll,
				...providers.map((provider: Provider) => {
					return {
						key: provider._id,
						value: `${provider.firstName} ${provider.lastName}`,
						roles: provider.roles,
					};
				}),
			];
		})
		.catch((err) => Promise.reject(err.response.data));
};

/** Get providers by program and OU from backend 2 */
const findProvidersV2 = async (
	programId: string[],
	orgUnitId: string[],
	includeAll: boolean,
): Promise<{ key: string; value: string; roles: string[] }[]> => {
	return instanceBo
		.get(
			`beneficiaries/providers?programIds=${programId.join(",")}&orgUnitIds=${orgUnitId.join(",")}`,
		)
		.then((res) => {
			const providers = res.data.body as Provider[];
			const optionAll = includeAll
				? [{ key: "all", value: "All", roles: [] }]
				: [];

			return [
				...optionAll,
				...providers.map((provider: Provider) => {
					return {
						key: provider._id,
						value: `${provider.firstName} ${provider.lastName}`,
						roles: provider.roles,
					};
				}),
			];
		})
		.catch((err) => Promise.reject(err?.response?.data || err));
};

const disenroll = async (
	data: {
		reasons: string;
		notes: string;
		userId: string;
	},
	beneficiaryId: string,
	enrollmentId: string,
): Promise<BeneficiaryItem> => {
	return axios
		.put(`/beneficiaries/disenroll/${beneficiaryId}/${enrollmentId}`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const reenroll = async (
	data: Partial<BeneficiaryPayload>,
	beneficiaryId: string,
	enrollmentId: string,
): Promise<{ beneficiaryId: string; enrollmentId: string }> => {
	return axios
		.put(`/beneficiaries/reenroll/${beneficiaryId}/${enrollmentId}`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const findByName = async (
	name: string,
	userId: string,
): Promise<BeneficiaryItemSearched[]> => {
	return axios
		.get(`/beneficiaries/search?data=${name}&userId=${userId}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const findListVisits = async (
	enrollmentId: string,
	rangeWeek: string,
): Promise<BeneficiaryVisitData[]> => {
	const [from, to] = rangeWeek.split("-");
	return instanceBo
		.get(`enrollments/${enrollmentId}/udt-results?from=${from}&to=${to}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const getCurrentWeekUdtStatus = async (
	enrollmentId: string,
	behaviorId?: string,
	treatmentConfigName?: string,
): Promise<IWeekStatus> => {
	const params = new URLSearchParams();
	if (behaviorId) {
		params.append("behaviorId", behaviorId);
	}
	if (treatmentConfigName) {
		params.append("treatmentConfigName", treatmentConfigName);
	}
	// Add cache-busting timestamp to prevent 304 Not Modified responses
	params.append("_t", Date.now().toString());

	const queryString = params.toString();
	const url = `/enrollments/${enrollmentId}/current-week${queryString ? `?${queryString}` : ""}`;

	return instanceBo
		.get(url)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const getEnrollmentPeriods = async (
	beneficiaryId: string,
): Promise<{ key: string; value: string }[]> => {
	return axios
		.get(`/beneficiaries/${beneficiaryId}/enrollment-periods`)
		.then((res) => {
			const response = res.data.body;

			const beneficiaryEnrollmentPeriods = response.map(
				(item: BeneficiaryEnrollmentPeriodsResponse) => {
					const value = `${item.program.name}: ${dayjs(item.startDate).format("MM/DD/YYYY")} - ${item.endDate ? dayjs(item.endDate).format("MM/DD/YYYY") : ""}`;

					return {
						key: item._id,
						value,
					};
				},
			);

			return beneficiaryEnrollmentPeriods;
		})
		.catch((err) => Promise.reject(err.response.data));
};

const findIncentiveHistory = async (
	enrollmentId: string,
	pageNumber: number,
	pageSize: number,
): Promise<BeneficiaryIncentiveHistoryResponse> => {
	return axios
		.get(
			`enrollments/${enrollmentId}/incentive-history?pageNumber=${pageNumber + 1}&pageSize=${pageSize}`,
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const deleteCard = async (params: ParamsDeleteCard): Promise<ICardDelete> => {
	return axios
		.patch(`/beneficiaries/${params.beneficiaryId}/cards/${params.cardId}`, {
			providerId: params.providerId,
		})
		.then((res) => {
			return res.data.body;
		})
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const getCards = async (beneficiaryId: string): Promise<CardData[]> => {
	return axios
		.get(`/beneficiaries/${beneficiaryId}/cards`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const postAddCard = async (
	beneficiaryId: string,
	data: CardPayload,
): Promise<CardData[]> => {
	return axios
		.post(`/beneficiaries/${beneficiaryId}/card`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const postSetPin = async (
	accountId: string,
	data: CardPinPayload,
): Promise<CardData[]> => {
	return axios
		.post("/cards/pin", data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const putBeneficiaryInvite = async (
	beneficiaryId: string,
): Promise<{ message: string }> => {
	return axios
		.put(`/beneficiaries/${beneficiaryId}/invite`, {})
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const updatePayerPlan = async (
	beneficiaryId: string,
	data: {
		payerId: string;
		oldMemberIdNumber: string;
		newMemberIdNumber: string;
		changedBy?: string;
	},
): Promise<{ message: string }> => {
	return instanceBo
		.patch(`beneficiaries/${beneficiaryId}/payer-plans`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const getBeneficiaryPayerPlans = (
	beneficiaryId: string,
): Promise<
	{
		payerId: string;
		memberIdNumber: string;
		createdAt: Date;
	}[]
> => {
	return instanceBo
		.get(`beneficiaries/${beneficiaryId}/payer-plans`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

export const beneficiaryService = {
	findAll,
	findAllV2,
	findById,
	findByIdV2,
	create,
	update,
	patchBeneficiary,
	updateReconfirm,
	getConsentForms,
	patchConsentForm,
	getConsentFormUrl,
	findPrograms,
	findProgramsV2,
	findProviderSites,
	findProviders,
	findProvidersV2,
	disenroll,
	reenroll,
	findByName,
	findListVisits,
	getCurrentWeekUdtStatus,
	getEnrollmentPeriods,
	findIncentiveHistory,
	deleteCard,
	getCards,
	postAddCard,
	postSetPin,
	putBeneficiaryInvite,
	updatePayerPlan,
	getBeneficiaryPayerPlans,
};
